import AsyncButton from "@components/button/AsyncButton.component";
import SaveButton from "@components/button/SaveButton.component";
import GithubConnectorConfig from "@components/connectors/GithubConnectorConfig.component";
import InfoAlert from "@components/statusIndicators/InfoAlert.component";
import FullResyncModal, { FullResyncOptions } from "@components/sources/FullResyncModal.component";
import SourceORM, { ConfigField, RecordTypeOptionResponse, SourceRecordType, SyncStatus } from "@models/source";
import ApiService, { JobEnqueueResponse, SingleRecordResponse } from "@services/api/api.service";
import AuthService from "@services/auth/auth.service";
import BackgroundService from "@services/bg.service";
import { invalidateEverything, useDataLoadsForSource, usePipelineNodes, useProjectConfig, useSourceRecordTypes, useSourceSetupScript, useSourceTypeConfig } from "@stores/data.store";
import { useSource } from "@stores/sources.store";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, ButtonGroup, Col, Dropdown, Form, Row, Spinner } from "react-bootstrap";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import styled from 'styled-components';
import { useImmer } from "use-immer";
import PageStructure, { PageContent, PageSidebar, Pane, PaneContent } from "./PageStructure.component";
import RecordInfo from "@components/card/RecordInfo.component";
import { SidebarNodeList } from "@components/nav/DagDataLibrary.component";
import { atomOneLight, CopyBlock } from "react-code-blocks";
import toast from "@services/toast.service";
import SuccessAlert from "@components/statusIndicators/SuccessAlert.component";
import DataLoadList from "@components/dataload/DataLoadList.component";
import { getErrorMessage } from "@services/errors.service";
import CronScheduleSelect from "@components/scheduler/CronSchedulerSelect.component";
import { closeWaiting, requireConfirmation, shareText, showWaiting } from "@services/alert/alert.service";
import NotFound from "@components/alert/NotFound.component";
import { FullPageLoader } from "@components/loaders/FullPageLoader.component";
import SourceIcon from "@components/sources/SourceIcon.component";
import ConfigFieldDescription from "@components/sources/ConfigFieldDescription.component";
import Warning from "@components/statusIndicators/Warning.component";
import { BigQueryTableMapping, TableStructure } from "@components/connectors/BigQueryTableSelector.component";
import EditableText from "@components/general/EditableText.component";
import Select from "react-select";


interface RecordTypeOptionSettings {
    enabled: boolean;
    primaryKey: string[];
    recordTypeInfo: RecordTypeOptionResponse;
}

interface RecordTypeOptionProps {
    rto: RecordTypeOptionResponse;
    enabled?: boolean;
    primaryKey?: string[];
    onToggle?: (val: boolean) => void;
}

const RecordTypeOptionStyles = styled.div`
display: flex;



.switch {
    width: 100px;
}

.label {
    flex: 1;
    font-weight: 600;
    font-family: "Poppins";
}

.primary-key {
    width: 250px;
}

&.disabled .label {
    color: var(--ct-text-muted) !important;
}
`


const Styles = styled.div`

.btn-actions {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: right;
    gap: 15px;
    width: 100%;
    align-items: center;
}
`

// Generic options type that can accommodate different field types
interface ConfigFieldOptions {
  [key: string]: any;
}

interface ConfigFieldProps {
    onChange: (newVal: string) => any;
    configField: ConfigField;
    secure?: boolean;
    value: string;
    options?: ConfigFieldOptions; // Generic options object that can contain anything
}

const ConfigFieldEditor = (props: ConfigFieldProps) => {
    const [showSecure, setShowSecure] = useState(false);

    if (props.configField.field_type == 'LONG_TEXT') {
        return <div className="col-md-12"><Form.Group className="mb-2">
            <Form.Label>{props.configField.label}</Form.Label>
            <div style={{ position: 'relative' }}>
                <Form.Control as="textarea" onChange={(e) => {
                    if (props.secure && !showSecure) {
                        setShowSecure(true);
                        return;
                    }
                    props.onChange(e.target.value)
                }}
                onFocus={() => { if (props.secure && !showSecure && props.value) setShowSecure(true); }}
                value={props.secure && !showSecure ? (props.value ? '••••••••' : '') : props.value}
                placeholder={props.secure ? 'Enter new value...' : undefined}
                />
                {props.secure && (
                    <button
                        type="button"
                        onClick={() => setShowSecure(!showSecure)}
                        style={{ position: 'absolute', top: '4px', right: '4px', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px', fontSize: '12px', opacity: 0.6 }}
                    >
                        {showSecure ? 'Hide' : 'Show'}
                    </button>
                )}
            </div>
            <ConfigFieldDescription description={props.configField.description} />
        </Form.Group></div>
    }

    if (props.configField.field_type == 'MULTI_SELECT') {
        const options = props.configField.choices?.map((choice) => ({
            key: choice,
            label: choice,
            value: choice
        })) || [];
        const selectedStreams = props.value ? props.value.split(',') : [];
        const selectedOptions = options.filter(opt => selectedStreams.includes(opt.value));
        return <div className="col-md-12"><Form.Group className="mb-2">
            <Form.Label>{props.configField.label}</Form.Label>
            <Select
                isMulti={true}
                value={selectedOptions}
                onChange={(selected) => {
                    const values = (selected as any[]).map(s => s.value);
                    props.onChange(values.join(','));
                }}
                options={options}
            />
            <ConfigFieldDescription description={props.configField.description} />
        </Form.Group></div>
    }

    if (props.configField.field_type == 'SINGLE_SELECT') {
        const options = props.configField.choices?.map((choice) => ({
            key: choice,
            label: choice,
            value: choice
        })) || [];
        const selectedValue = props.value || props.configField.default_value;
        const selectedOption = options.find(opt => selectedValue === opt.value);
        return <div className="col-md-6"><Form.Group className="mb-2">
            <Form.Label>{props.configField.label}</Form.Label>
            <Select
                isMulti={false}
                value={selectedOption}
                onChange={(selected) => {
                    props.onChange(selected ? (selected as any).value : '');
                }}
                options={options}
            />
            <ConfigFieldDescription description={props.configField.description} />
        </Form.Group></div>
    }

    if (props.configField.field_type === 'BIGQUERY_TABLE_MAPPING') {
        return (
        <BigQueryTableMapping
            value={props.value}
            onChange={props.onChange}
            label={props.configField.label}
            tableStructure={props.options as TableStructure | undefined}
        />
        );
  }

    return <div className="col-md-6"><Form.Group className="mb-2">
        <Form.Label>{props.configField.label}</Form.Label>
        <Form.Control type={(props.secure) ? 'password': 'text'} onChange={(e) => {
            props.onChange(e.target.value)
        }} value={props.value}/>
        <ConfigFieldDescription description={props.configField.description} />
    </Form.Group> </div>
}


const RecordTypeOption = (props: RecordTypeOptionProps) => {
    return <RecordTypeOptionStyles className={props.enabled ? 'enabled' : 'disabled'}>
        <div className="switch">
            <Form.Check
                type="switch"
                label=""
                checked={!!props.enabled}
                onChange={(e) => {
                    if (props.onToggle) {
                        props.onToggle!(!props.enabled);
                    }
                }}
            />
        </div>
        <div className="label">
            {props.rto.label}
        </div>
    </RecordTypeOptionStyles>
}

const SourceConfigPage = () => {
    const navigate = useNavigate();
    const [query, setQuery] = useSearchParams();
    const {sourceId} = useParams();

    const source = useSource(sourceId as string);

    const [triggeringSync, setTriggeringSync] = useState(false);
    const [showFullResyncModal, setShowFullResyncModal] = useState(false);

    const {refetch: refetchDataloads} = useDataLoadsForSource(sourceId as string);

    const sourceRecordTypes = useSourceRecordTypes(sourceId as string);
    const sourceTypeConfig = useSourceTypeConfig(source.data ? source.data.type : '');
    const sourceSetupScript = useSourceSetupScript(source.data ? source.data.id as string: '');

    const refreshSourceFivetranStatus = useCallback(async () => {
        const result = await ApiService.getInstance().request('GET', `/sources/${sourceId}/refresh`)
        source.refetch();
    }, [sourceId]);

    const [rtoSettings, setRtoSettings] = useImmer<{
        [key: string]: RecordTypeOptionSettings,
    }>({});

    const [config, setConfig] = useImmer<{
        [key: string]: any
    }>({});

    const [secretConfig, setSecretConfig] = useImmer<{
        [key: string]: any
    }>({});

    const [syncCronExpression, setSyncCronExpression] = useState('');

    useEffect(() => {
        if (query.get('authorized') == 'true' && config && Object.keys(config).length > 0) {
            toast('success', 'Success', 'Authorization successful.');
            setQuery({});
            // need to wait for config to be set
            save();
            return;
        }

        if (query.get('error') == 'authorization_error') {
            toast('error', 'Error', 'Error authorizing connection.');
            setQuery({});
            return;
        }
        
    }, [query, config]);

    useEffect(() => {
        if (source.data) {
            setConfig(source.data.config);

            if (source.data.sync_info && source.data.sync_info.sync_schedule.cron_expression) {
                setSyncCronExpression(source.data.sync_info.sync_schedule.cron_expression);
            }
        }
    }, [source.dataUpdatedAt]);

    useEffect(() => {
        if (!sourceTypeConfig.data || !sourceRecordTypes.data || !sourceTypeConfig.data || !sourceTypeConfig.data.exposed_record_types) {
            return;
        }

        setRtoSettings(draft => {
            sourceTypeConfig.data!.exposed_record_types?.forEach((rt: RecordTypeOptionResponse) => {
                // Is there an existing record type?
                const existing = sourceRecordTypes.data.find(srt => srt.resource_name === rt.name);

                draft[rt.name] = {
                    enabled: !!existing,
                    primaryKey: rt.default_primary_key,
                    recordTypeInfo: rt,
                }
            });
        });
    }, [sourceTypeConfig.dataUpdatedAt, sourceRecordTypes.dataUpdatedAt]);

    const isSyncing = useMemo(() => {
        return [SyncStatus.Queued, SyncStatus.Syncing].includes(source.data?.sync_info?.status as SyncStatus);
    }, [source.dataUpdatedAt]);

    const syncButtonText = useMemo(() => {
        if (triggeringSync) {
            return 'Queuing';
        }else if (source.data?.sync_info?.status == SyncStatus.Queued) {
            return 'Queued';
        }else if (source.data?.sync_info?.status == SyncStatus.Syncing) {
            return 'Syncing';
        }else{
            return 'Sync Now';
        }
    }, [triggeringSync, source.dataUpdatedAt]);

    const [maxPollingDuration, setMaxPollingDuration] = useState(7200000); // 2 hours in milliseconds
    const [pollingStartTime, setPollingStartTime] = useState<number | null>(null);
    
    // Configuration for exponential backoff to track sync 
    const INITIAL_INTERVAL = 5000; // Start with 5 seconds
    const MAX_INTERVAL = 30000; // Max of 30 seconds
    const BACKOFF_RATE = 1.5; // Each interval will be 1.5x longer
    const [currentInterval, setCurrentInterval] = useState(INITIAL_INTERVAL);
    
    // useEffect to handle the polling for sync updates with exponential backoff
    useEffect(() => {
        let timeoutId: NodeJS.Timeout | null = null;
    
        const pollForUpdates = async () => {
            if (!pollingStartTime || !isSyncing) {
                return;
            }
    
            const currentTime = Date.now();
            if (currentTime - pollingStartTime > maxPollingDuration) {
                // Stop polling if we've exceeded the max duration
                setPollingStartTime(null);
                setCurrentInterval(INITIAL_INTERVAL); // Reset interval for next time
                return;
            }
    
            // Refresh the data
            await source.refetch();
            await refetchDataloads();
            await nodes.refetch();
    
            // Calculate next interval with exponential backoff
            const nextInterval = Math.min(currentInterval * BACKOFF_RATE, MAX_INTERVAL);
            setCurrentInterval(nextInterval);
    
            // Schedule next poll
            timeoutId = setTimeout(pollForUpdates, nextInterval);
        };
    
        if (isSyncing && pollingStartTime) {
            // Initial poll
            timeoutId = setTimeout(pollForUpdates, currentInterval);
        } else {
            // Reset interval when sync stops
            setCurrentInterval(INITIAL_INTERVAL);
        }
    
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [isSyncing, pollingStartTime, currentInterval, maxPollingDuration]);
    
    const triggerManualSync = useCallback(async (full_refresh: boolean = false, options?: { drop_schema_first?: boolean; selected_streams?: string[] }) => {
        setTriggeringSync(true);
        try {
            await SourceORM.triggerManualSync(sourceId as string, full_refresh, options);
            // Reset interval and start polling
            setCurrentInterval(INITIAL_INTERVAL);
            setPollingStartTime(Date.now());
        } catch (error) {
            toast('danger', 'Error', getErrorMessage(error));
        } finally {
            setTriggeringSync(false);
            // Initial data refresh
            source.refetch();
            refetchDataloads();
        }
    }, [sourceId]);

    const handleFullResync = useCallback(async (options: FullResyncOptions) => {
        setShowFullResyncModal(false);
        await triggerManualSync(true, {
            drop_schema_first: options.dropSchemaFirst,
            selected_streams: options.selectedStreams || undefined,
        });
    }, [triggerManualSync]);

    const changeConfig = useCallback((key: string, val: any) => {
        setConfig(draft => {
            draft[key] = val;
        });
    }, []);

    const changeSecretConfig = useCallback((key: string, val: any) => {
        setSecretConfig(draft => {
            draft[key] = val;
        });
    }, []);

    const isPaused = useMemo(() => {
        if (source.data?.fivetran) {
            return !!source.data?.fivetran?.connector.paused
        }else{
            return !source.data?.sync_info?.sync_schedule.enabled;
        }
    }, [source.dataUpdatedAt]);

    const save = useCallback(async () => {
        const srts = Object.keys(rtoSettings).map(k => {
            if (rtoSettings[k].enabled) {
                return {
                    composite_key: rtoSettings[k].primaryKey,
                    resource_name: k,
                    source_id: '',
                    name: rtoSettings[k].recordTypeInfo.label,
                };
            }
            return null;
            
        }).filter(d => !!d);
        try {
            let hasSecureConfig = false;
            Object.keys(secretConfig).forEach(k => {
                if (!!secretConfig[k]) {
                    hasSecureConfig = true;
                }
            });

            const jobResult = await SourceORM.init(sourceId as string, {
                source_record_types: srts,
                config: config,
                secure_config: hasSecureConfig ? secretConfig : null,
                sync_info: {
                    sync_schedule: {
                        cron_expression: syncCronExpression
                    }
                }
            });
            nodes.refetch();
            toast('success', 'Success', 'Configuration saved');

        } catch (err) {
            console.error(err);
            toast('danger', 'Error', getErrorMessage(err));
        } finally {
            source.refetch();

        }

    }, [source.dataUpdatedAt, config, secretConfig, rtoSettings, syncCronExpression]);

    const [pausing, setPausing] = useState(false);
    const togglePause = useCallback(async () => {
        if (!source.data) {
            return;
        }
        setPausing(true);
        await SourceORM.toggleSync(source.data.id as string, !isPaused)
        setPausing(false);

        source.refetch();
    }, [source.dataUpdatedAt, sourceId, isPaused])

    const checkForRefresh = useCallback(() => {
        console.log('Checking for refresh', source.data?.fivetran?.connector.status.sync_state);
        if (source.data && (['scheduled', 'syncing'].includes(source.data.fivetran?.connector.status.sync_state as string))) {
            refreshSourceFivetranStatus();
        }
    }, [refreshSourceFivetranStatus, source.dataUpdatedAt]);

    useEffect(() => {
        const interval = setInterval(() => {
            checkForRefresh();
        }, 5000)
        
        return () => {
            clearInterval(interval);
        }
    }, []);

    const deleteSource = useCallback(async () => {
        const confirmed = await requireConfirmation(`Are you sure you want to delete this source and all its data? Type out the full source name ("${source.data?.name}") to continue.`, 'Delete Source', 'Delete', 'Cancel', source.data?.name);
        if (confirmed) {
            showWaiting({message: `Deleting Source ${source.data?.name}...`});
            await SourceORM.deleteById(sourceId as string);
            invalidateEverything();
            closeWaiting();
            navigate('/');
            toast('success', 'Source deleted', 'Source deleted successfully.');
        }
    }, [sourceId, source.dataUpdatedAt]);


    const [loadingConnectorCard, setLoadingConnectorCard] = useState(false);

    const startConnectorCardFlow = useCallback(async () => {
        setLoadingConnectorCard(true);
        const resp = (await SourceORM.getConnectorCardUrl(sourceId as string, '/'));
        window.location.href = resp.connect_card_url;
        setLoadingConnectorCard(false);
    }, [sourceId]);

    const startAuthorizationFlow = useCallback(async () => {
        setLoadingConnectorCard(true);
        await save();
        const resp = (await SourceORM.getConnectorAuthorizationUrlSource(sourceId as string, '/'));
        window.location.href = resp.authorization_url;
        setLoadingConnectorCard(false);
    }, [sourceId, source.data, save]);

    const [loadingShareableLink, setLoadingShareableLink] = useState(false);
    const [showShareableLinkModal, setShowShareableLinkModal] = useState(false);
    const [shareableLink, setShareableLink] = useState('');

    const generateShareableLink = useCallback(async () => {
        setLoadingShareableLink(true);
        setShowShareableLinkModal(true);
        // auto save
        await save();
        const resp = await SourceORM.createShareableAuthorizationUrl(sourceId as string);
        setShareableLink(resp.shareable_auth_url);
        console.log(resp.shareable_auth_url);
        shareText({
            onClose: () => { },
            header: 'Shareable Authorization Link',
            label: 'Authorization Link',
            text: "\n" +resp.shareable_auth_url,
        })
        setLoadingShareableLink(false);

    }, [sourceId, source.data, save]);

    const nodes = usePipelineNodes();

    const projectConfig = useProjectConfig();

    const nodesInThisSource = useMemo(() => {
        if (!nodes.data) {
            return [];
        }

        return nodes.data.filter(n => n.source_id == sourceId || n.secondary_source_ids?.includes(sourceId as string));
    }, [nodes.dataUpdatedAt, sourceId]);

    const editSourceName = useCallback(async (newName: string) => {
        if(newName.trim() == '' || !source.data) {
            return;
        }
        await SourceORM.save({
            id: sourceId as string,
            name: newName,
            type: source.data?.type as string,
        });
        source.refetch();
    }, [source.dataUpdatedAt, config, secretConfig, rtoSettings, syncCronExpression]);

        const editSourceDescription = useCallback(async (newDescription: string) => {
        if(newDescription.trim() == '' || !source.data) {
            return;
        }
        await SourceORM.save({
            id: sourceId as string,
            name: source.data?.name as string,
            description: newDescription,
            type: source.data?.type as string,
        });
        source.refetch();
    }, [source.dataUpdatedAt, config, secretConfig, rtoSettings, syncCronExpression]);



    if (source.isLoading || sourceTypeConfig.isLoading) {
        return <FullPageLoader />
    }

    let innerContent = <div>An error occurred</div>;

    if (source.isError || source.data == null) {
        let errMesaage = <div>An error occurred</div>;
        const httpError = source.error as { code: number } ;
        // Check if it's a 404 error
        if (httpError && httpError.code === 404) {
            errMesaage = <NotFound resourceName="Source" />;
        }
        
        // Handle other errors
        innerContent = <PageContent>
            <Pane>
                <PaneContent>
                    {errMesaage}
                </PaneContent>
            </Pane>
        </PageContent>

    }else if (!sourceTypeConfig.data) {
        return <div>Missing Resources</div>;
    }else{
        innerContent = (<>
        <PageSidebar big={true}>
            <Pane>
                <PaneContent>
                    <div className="p-3">
                        <div className="d-flex center-vertically">
                            <SourceIcon sourceId={source.data.id as string} className="me-2"/>
                            <div className="flex-1">
                                <h1 className="mb-0">
                                    <EditableText value={source.data.name} onChange={editSourceName} />    
                                </h1>
                                <div className="text-muted font-13">
                                    <EditableText value={source.data.description as string} emptyPlaceholder="No description." onChange={editSourceDescription} /> 
                                </div>
                            </div>
                        </div>
                        
                        <hr />
                        {nodesInThisSource.length == 0 && <>
                            <></>
                        </>}
                        <SidebarNodeList nodes={nodesInThisSource}/>
                    </div>
                </PaneContent>
            </Pane>
        </PageSidebar>
        <PageContent>
        
            <Pane>
                
                <PaneContent>
                    <Styles>
                    
                    
                    <div className="p-3">
                        <div className="btn-actions mb-3">
                            <button onClick={deleteSource} className="btn btn-outline-danger me-1">
                                <i className="mdi mdi-delete"></i> Delete Source
                            </button>
                            {source.data?.type === 'plb_quickbooks_desktop' && <>
                                {isSyncing ? (
                                    <AsyncButton
                                        icon="mdi mdi-reload"
                                        variant="success"
                                        disabled
                                        onClick={() => {}}
                                        text="Syncing"
                                        loading
                                    />
                                ) : (
                                    <Dropdown as={ButtonGroup}>
                                        <span className="btn btn-light" style={{ opacity: 1, pointerEvents: 'none' }}>
                                            <i className="mdi mdi-clock-outline me-1"></i>
                                            {source.data?.last_loaded
                                                ? `Last synced ${(() => {
                                                    const diff = Date.now() - new Date(source.data.last_loaded).getTime();
                                                    const mins = Math.floor(diff / 60000);
                                                    if (mins < 1) return 'just now';
                                                    if (mins < 60) return `${mins}m ago`;
                                                    const hrs = Math.floor(mins / 60);
                                                    if (hrs < 24) return `${hrs}h ago`;
                                                    const days = Math.floor(hrs / 24);
                                                    return `${days}d ago`;
                                                })()}`
                                                : 'Never synced'}
                                        </span>
                                        <Dropdown.Toggle split variant="light" id="dropdown-split-basic" />
                                        <Dropdown.Menu>
                                            <Dropdown.Item onClick={() => setShowFullResyncModal(true)}>Full Re-Sync</Dropdown.Item>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                )}
                            </>}
                            {source.data?.type !== 'plb_quickbooks_desktop' && isSyncing && ['meltano', 'pliable'].includes(sourceTypeConfig.data.provider) && <AsyncButton
                                icon={`mdi mdi-reload`}
                                variant={(triggeringSync || isSyncing) ? 'success' : 'light'}
                                disabled={triggeringSync}
                                onClick={triggerManualSync}
                                text={syncButtonText}
                                loading={triggeringSync || isSyncing}
                                testId="trigger-manual-sync"
                            />}
                            {source.data?.type !== 'plb_quickbooks_desktop' && !isSyncing && ['meltano', 'pliable'].includes(sourceTypeConfig.data.provider) && <Dropdown as={ButtonGroup}>
                                <AsyncButton
                                    icon={`mdi mdi-reload`}
                                    variant={(triggeringSync || isSyncing) ? 'success' : 'light'}
                                    disabled={triggeringSync}
                                    onClick={triggerManualSync}
                                    text={syncButtonText}
                                    loading={triggeringSync || isSyncing}
                                    testId="trigger-manual-sync"
                                />

                                <Dropdown.Toggle split variant="light" id="dropdown-split-basic" />

                                <Dropdown.Menu>
                                    <Dropdown.Item onClick={() => setShowFullResyncModal(true)}>Full Re-Sync</Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown> }

                            <SaveButton onClick={save}/>
                            
                        </div>
                        {sourceTypeConfig.data.provider == 'fivetran' && <>
                            <h2>Configuration</h2>
                            <InfoAlert>
                                <div>
                                    This data source is managed by our partners at Fivetran. Please contact your Pliable account rep if you'd like to make changes to the configuration.
                                </div>
                            </InfoAlert>
                            
                            <AsyncButton
                                loading={loadingConnectorCard}
                                text="Authorize Connection"
                                className="btn-pliable" 
                                onClick={() => startConnectorCardFlow()}
                            />
                        </>}
                        {sourceTypeConfig.data.provider == 'pliable' && <>
                            {!sourceTypeConfig.data.additional_config && !sourceTypeConfig.data.additional_secret_config && <>
                                <div className="card mb-3">
                                    <div className="card-body">
                                        No configuration needed!
                                    </div>
                                </div>
                            </>}
                            {(sourceTypeConfig.data.additional_config || []).length > 0 && <>
                                <div className="card mb-3">
                                    <div className="card-body">
                                        <h2>Configuration</h2>
                                        {sourceTypeConfig.data.additional_config?.map(c => {
                                            return <div className="col-md-6"><ConfigFieldEditor
                                                onChange={(newVal: string) => {
                                                    changeConfig(c.name, newVal);
                                                }}
                                                value={config.hasOwnProperty(c.name) ? config[c.name] : ''}
                                                configField={c}
                                            /> </div>
                                        })}
                                    </div>
                                </div>
                                
                            </>}
                            {(sourceTypeConfig.data.additional_secret_config || []).length > 0 && <>
                                <div className="card mb-3">
                                    <div className="card-body">
                                        <h2>Secure Configuration</h2>
                                        <p>This data is stored securely and cannot be displayed. You can change the values by entering in new data here.</p>
                                        {sourceTypeConfig.data.additional_secret_config?.map(c => {
                                            return <ConfigFieldEditor
                                                onChange={(newVal: string) => {
                                                    changeSecretConfig(c.name, newVal);
                                                }}
                                                value={secretConfig.hasOwnProperty(c.name) ? secretConfig[c.name] : ''}
                                                configField={c}
                                                secure={true}
                                            />
                                        })}
                                    </div>
                                </div>
                                
                            </>}
                            {source.data.type == 'plb_quickbooks_desktop' && <>
                                <div className="card mb-3">
                                    <div className="card-body">
                                        <h2>QuickBooks Web Connector</h2>
                                        <p>Download the .qwc configuration file and add it to the QuickBooks Web Connector on the machine running QuickBooks Desktop.</p>
                                        <ol className="mb-3">
                                            <li>Save your configuration above and click "Download .qwc File"</li>
                                            <li>Open the QuickBooks Web Connector on your Windows machine</li>
                                            <li>Click "Add an application" and select the downloaded .qwc file</li>
                                            <li>Enter the QBWC password you configured above when prompted</li>
                                        </ol>
                                        <AsyncButton
                                            icon="mdi mdi-download"
                                            variant="primary"
                                            text="Download .qwc File"
                                            onClick={async () => {
                                                try {
                                                    const response = await fetch(
                                                        `${ApiService.getInstance().hostname}${ApiService.getInstance().basePath}/qbwc/download-qwc/${sourceId}`,
                                                        {
                                                            headers: {
                                                                'Authorization': 'Bearer ' + AuthService.getInstance().getAccessToken(),
                                                            },
                                                        }
                                                    );
                                                    if (!response.ok) throw new Error('Download failed');
                                                    const blob = await response.blob();
                                                    const url = URL.createObjectURL(blob);
                                                    const a = document.createElement('a');
                                                    a.href = url;
                                                    a.download = `pliable_${source.data?.name?.replace(/\s+/g, '_').replace(/\//g, '_') || 'quickbooks'}_${sourceId}.qwc`;
                                                    document.body.appendChild(a);
                                                    a.click();
                                                    document.body.removeChild(a);
                                                    URL.revokeObjectURL(url);
                                                    toast('success', 'Success', 'Downloaded .qwc file');
                                                } catch (e) {
                                                    toast('danger', 'Error', 'Failed to download .qwc file');
                                                }
                                            }}
                                        />
                                        <AsyncButton
                                            icon="mdi mdi-link-variant"
                                            variant="outline-primary"
                                            className="ms-2"
                                            text="Share Link"
                                            onClick={async () => {
                                                try {
                                                    const result = await ApiService.getInstance().request(
                                                        'POST',
                                                        `/qbwc/share-qwc/${sourceId}`,
                                                    );
                                                    await navigator.clipboard.writeText(result.url);
                                                    toast('success', 'Success', 'Link copied to clipboard (expires in 7 days)');
                                                } catch (e) {
                                                    toast('danger', 'Error', 'Failed to generate share link');
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            </>}
                            {source.data.type == 'plb_tracker' && <>
                                <div className="card mb-3">
                                    <div className="card-body">
                                        <h2>Usage</h2>
                                        <p>Copy the script below and paste it into the <code>&lt;head&gt;</code> of your website. You can also add it to your Google Tag Manager.</p>
                                        {source.data.static_config?.api_endpoint && <>
                                            <CopyBlock
                                                language="html"
                                                text={`<script src="https://cdn.static.pliable.co/plb_tracker.min.js" data-endpoint="${projectConfig.data?.infra.plb_tracker?.api_endpoint}"></script>`}
                                                showLineNumbers={false}
                                                theme={atomOneLight}
                                                codeBlock={true}
                                            />
                                        </>}
                                        {!source.data.static_config?.api_endpoint && <>
                                            <Warning>
                                                <div>
                                                    Pliable Tracker has not been properly configured for this account (missing endpoint). Please contact support.
                                                </div>
                                                </Warning>
                                                
                                        </>}
                                        
                                    </div>
                                </div>
                            </>}

                            {!!sourceSetupScript.data && <>
                                <div className="card">
                                    <div className="card-body">
                                        <h2>Setup Script</h2>

                                        <div style={{maxHeight: 400, overflowX: 'scroll'}} className="mb-3">
                                        <CopyBlock
                                            text={sourceSetupScript.data as string}
                                            language="sql"
                                            showLineNumbers={false}
                                            theme={atomOneLight}
                                            codeBlock={true}
                                        />
                                        </div>
                                    </div>
                                </div>
                            </>}
                            <DataLoadList sourceId={sourceId as string}/>
                        </>}
                        {sourceTypeConfig.data.provider == 'meltano' && <>
                            {(sourceTypeConfig.data.additional_config || []).length > 0 && <>
                                <div className="card mb-3">
                                    <div className="card-body">
                                        <h2>Configuration</h2>
                                        <div className="row mb-2">
                                        {sourceTypeConfig.data.additional_config?.map(c => {
                                            return <ConfigFieldEditor
                                                onChange={(newVal: string) => {
                                                    changeConfig(c.name, newVal);
                                                }}
                                                
                                                value={config.hasOwnProperty(c.name) ? config[c.name] : ''}
                                                options={config.hasOwnProperty(`${c.name}_options`) ? JSON.parse(config[`${c.name}_options`]) : ''}
                                                configField={c}
                                            />  
                                        })}
                                        </div>
                                    </div>
                                </div>
                            </>}

                            {(sourceTypeConfig.data.additional_secret_config || []).length > 0 && <>
                                    <div className="card mb-3">
                                        <div className="card-body">
                                            <h2>Secure Configuration</h2>
                                            <p>This data is stored securely and cannot be displayed. You can change the values by entering in new data here.</p>
                                            {sourceTypeConfig.data.additional_secret_config?.map(c => {
                                                return <ConfigFieldEditor
                                                    onChange={(newVal: string) => {
                                                        changeSecretConfig(c.name, newVal);
                                                    }}
                                                    value={secretConfig.hasOwnProperty(c.name) ? secretConfig[c.name] : ''}
                                                    configField={c}
                                                    secure={true}
                                                />
                                            })}
                                        </div>
                                    </div>
                                </>}

                            {(!sourceTypeConfig.data.oauth || source.data.oauth_last_authorized) && <>
                                <div className="card mb-3">
                                    <div className="card-body d-flex">
                                        <div className="flex-fill">
                                            <CronScheduleSelect
                                                label="Sync Schedule"
                                                scheduleEnabled={!isPaused}
                                                value={syncCronExpression} 
                                                isDisabled={pausing}
                                                onChange={setSyncCronExpression}
                                                onToggle={togglePause}
                                             />
                                        </div>
                                    </div>
                                </div>
                            </>}

                            {source.data.oauth_last_authorized && <>
                                <div className="card mb-3">
                                    <div className="card-body d-flex">
                                        <div className="flex-fill" style={{padding: '1.2rem'}}>
                                            <div className="btn-actions">
                                                    <Dropdown>
                                                        <Dropdown.Toggle variant="success" id="dropdown-basic">
                                                            Connected
                                                        </Dropdown.Toggle>

                                                        <Dropdown.Menu>
                                                            <Dropdown.Item onClick={() => startAuthorizationFlow()}>{`Re-Authorize ${sourceTypeConfig.data.title}`}</Dropdown.Item>
                                                            <Dropdown.Item onClick={() => generateShareableLink()}>{`Create Shareable Link`}</Dropdown.Item>
                                                        </Dropdown.Menu>
                                                    </Dropdown>
                                                    
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>}

                            {!source.data.oauth_last_authorized && sourceTypeConfig.data.oauth &&  <>
                                <div className="card mb-3">
                                    <div className="card-body">
                                        <div className="btn-actions">
                                            {sourceTypeConfig.data.oauth && (
                                                <Dropdown>
                                                <Dropdown.Toggle variant="pliable" id="dropdown-basic">
                                                    Auth Required
                                                </Dropdown.Toggle>

                                                <Dropdown.Menu>
                                                    <Dropdown.Item onClick={() => startAuthorizationFlow()}>{`Authorize ${sourceTypeConfig.data.title}`}</Dropdown.Item>
                                                    <Dropdown.Item onClick={() => generateShareableLink()}>{`Create Shareable Link`}</Dropdown.Item>
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        )}
                                        </div>
                                    </div>
                                </div>
                            </>}
                            
                            

                            {!!sourceSetupScript.data && <>  
                                <div className="card mb-3">
                                    <div className="card-body">
                                        <h2>Setup Script</h2>

                                        <div style={{maxHeight: 400, overflowX: 'scroll'}} className="mb-3">
                                        <CopyBlock
                                            text={sourceSetupScript.data as string}
                                            language="sql"
                                            showLineNumbers={false}
                                            theme={atomOneLight}
                                            codeBlock={true}
                                        />
                                        </div>
                                    </div>
                                </div>
                            </>}
                            <DataLoadList sourceId={sourceId as string}/>
                        </>}
                    </div>
                    </Styles>
                </PaneContent>
            </Pane>
        </PageContent>
        <FullResyncModal
            sourceId={sourceId as string}
            show={showFullResyncModal}
            onClose={() => setShowFullResyncModal(false)}
            onConfirm={handleFullResync}
        />
        </>);
    }

    return <PageStructure
        pageTitle="Configure Data Source"
    >
        {innerContent}
    </PageStructure>
}

export default SourceConfigPage;